import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { postData, ImageFileUpload } from "../../../utility/Utility"; // Adjust this import if needed
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

export default function AddCategory() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    categoryImage: null, // File object
    imagePath: "", // URL/path returned after upload
    backgroundImage: null, // Background image file object
    backgroundImagePath: "", // Background image URL/path returned after upload
    slug: "",
    isActive: true,
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "categoryImage") {
      setFormData({ ...formData, categoryImage: files[0] });
    } else if (name === "backgroundImage") {
      setFormData({ ...formData, backgroundImage: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const toggleActive = () => {
    setFormData({ ...formData, isActive: !formData.isActive });
  };

  // Function to upload image
  const uploadImage = async () => {
    if (!formData.categoryImage) return null;
    const imageFormData = new FormData();
    imageFormData.append("file", formData.categoryImage);

    try {
      const response = await ImageFileUpload("/api/admin/upload?is_upload=s3", imageFormData);
      const data = await response;
      if (response?.success) {
        return data.url || data.imageUrl || data.filePath; // Adjust based on your API response
      } else {
        toast.error(data.message || "Image upload failed");
        return null;
      }
    } catch (error) {
      toast.error("Network error during image upload");
      return null;
    }
  };

  // Function to upload background image
  const uploadBackgroundImage = async () => {
    if (!formData.backgroundImage) return null;
    const imageFormData = new FormData();
    imageFormData.append("file", formData.backgroundImage);

    try {
      const response = await ImageFileUpload("/api/admin/upload?is_upload=s3", imageFormData);
      const data = await response;
      if (response?.success) {
        return data.url || data.imageUrl || data.filePath; // Adjust based on your API response
      } else {
        toast.error(data.message || "Background image upload failed");
        return null;
      }
    } catch (error) {
      toast.error("Network error during background image upload");
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const uploadedImagePath = await uploadImage();
      const uploadedBackgroundImagePath = await uploadBackgroundImage();

      if (formData.categoryImage && !uploadedImagePath) {
        setLoading(false);
        return;
      }

      if (formData.backgroundImage && !uploadedBackgroundImagePath) {
        setLoading(false);
        return;
      }

      const payload = {
        title: formData.title,
        description: formData.description,
        slug: formData.slug,
        image: uploadedImagePath,
        backgroundImage: uploadedBackgroundImagePath,
        status: formData.isActive ? "active" : "inactive",
      };
      // Send category data
      const response = await postData("api/categories/create", payload);

      if (response.status) {
        toast.success("Category added successfully!");
        navigate(-1);
      } else {
        toast.error(response.message || "Failed to add category");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      console.error("Add category error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          {/* Header & Breadcrumbs */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            {/* ... */}
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              <i className="fas fa-arrow-left me-1"></i> Back
            </button>
          </div>

          {/* Form Card */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                {/* Card Header */}
                <div className="card-header">
                  <h5 className="card-title mb-0">Create New Category</h5>
                </div>
                {/* Card Body */}
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Title */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="title" className="form-label fw-medium">
                          Category Title <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="title"
                          name="title"
                          className="form-control"
                          placeholder="Enter category title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          minLength={3}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Slug */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="slug" className="form-label fw-medium">
                          Slug <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="slug"
                          name="slug"
                          className="form-control"
                          placeholder="Enter slug"
                          value={formData.slug}
                          onChange={handleInputChange}
                          required
                          pattern="[a-z0-9-]+"
                          title="Only lowercase letters, numbers and hyphens allowed"
                        />
                        <small className="text-muted">URL-friendly identifier</small>
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label
                          htmlFor="categoryImage"
                          className="form-label fw-medium"
                        >
                          Category Image <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          id="categoryImage"
                          name="categoryImage"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/*"
                          required
                        />
                        <small className="text-muted d-block mt-1">
                          Recommended: 800x600px, Max 2MB (JPEG/PNG)
                        </small>
                        {formData.categoryImage && (
                          <div className="mt-2">
                            <img
                              src={URL.createObjectURL(formData.categoryImage)}
                              alt="Preview"
                              className="img-thumbnail"
                              style={{ maxWidth: "200px", maxHeight: "150px" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {/* backgroundImage  Upload */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label
                          htmlFor="backgroundImage"
                          className="form-label fw-medium"
                        >
                          Background Image  <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          id="backgroundImage"
                          name="backgroundImage"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/*"
                          required
                        />
                        <small className="text-muted d-block mt-1">
                          Recommended: 800x600px, Max 2MB (JPEG/PNG)
                        </small>
                        {formData.backgroundImage && (
                          <div className="mt-2">
                            <img
                              src={URL.createObjectURL(formData.backgroundImage)}
                              alt="Preview"
                              className="img-thumbnail"
                              style={{ maxWidth: "200px", maxHeight: "150px" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status Switch */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">Status</label>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check form-switch d-flex align-items-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="isActive"
                            checked={formData.isActive}
                            onChange={toggleActive}
                            role="switch"
                            style={{ width: "2.5em", height: "1.25em" }}
                          />
                          <label className="form-check-label ms-2" htmlFor="isActive">
                            {formData.isActive ? (
                              <span className="text-success">Active</span>
                            ) : (
                              <span className="text-muted">Inactive</span>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="description" className="form-label fw-medium">
                          Description <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <textarea
                          className="form-control"
                          id="description"
                          name="description"
                          rows={4}
                          placeholder="Describe the category..."
                          value={formData.description}
                          onChange={handleInputChange}
                          required
                          minLength={10}
                        />
                        <small className="text-muted">Minimum 10 characters</small>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row mt-4">
                      <div className="col-md-6 offset-md-3">
                        <button
                          type="submit"
                          className="btn btn-primary py-2"
                          disabled={loading}
                        >
                          {loading ? "Processing..." : "Submit"}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}